// How close a finger has to be, and which mark it meant.
//
// #66. The bus stop circles are the smallest tappable thing on the map:
// `circle-radius` runs 3 px at zoom 14 to 7 px at zoom 18, and MapLibre counts
// the 2 px stroke in a circle's hit test, so the disc a tap must land inside is
// 10 px across where the layer starts and 18 px at the zoom people rarely
// reach. `map.on('click', LAYER, …)` queries the tap point exactly. The
// comment on PIN_SEPARATION_PX in map.ts already names the number that should
// have been met — 24 px, from the Lighthouse audit that measured 11.4 px of
// clickable space between the airport and Dyce station — and a stop is under
// it at every zoom it draws at.
//
// A mouse never noticed. It is pixel-precise, and the `mouseenter` handler
// turns the cursor into a pointer, so on a desktop you can see you are on
// target before you commit. A finger has neither: the contact patch is
// millimetres, the point the browser reports can be some way from where the
// reader believes they touched, and nothing signals a near miss. Chrome's own
// touch adjustment cannot help, because the whole map is one <canvas> and it
// has no per-feature targets to snap to. So this was correct on the machine it
// was written on and wrong on the device most readers hold — and nothing in
// the suite could tell those apart, which is the shape CLAUDE.md keeps
// describing.
//
// The rule here is stated about the reader's finger rather than about the
// drawn mark: a stop is tappable if its CENTRE is within half an accessible
// target of the tap. That makes the target exactly TAP_TARGET_PX across at
// every zoom, rather than something that changes size as the circle grows —
// and it means this file never has to know what the radius expression says.
//
// Its own module, importing nothing, for the reason map-style.ts gives: map.ts
// pulls in maplibre-gl and a stylesheet, neither of which survives a test
// runner, and these are the numbers most worth testing.

/**
 * The accessible target size, in CSS pixels.
 *
 * Not chosen here — it is the number map.ts already cites as the bar it failed
 * to clear, and the one WCAG 2.5.5 states. Written once so the two places
 * cannot drift into disagreeing about what a target is.
 */
export const TAP_TARGET_PX = 24;

/**
 * How far from a mark's centre a tap may land and still mean it.
 *
 * Zero for a fine pointer, deliberately. A mouse is precise and hovers, so
 * widening its target would only make it claim marks the reader can see
 * perfectly well they are not pointing at — the desktop behaviour is correct
 * and this changes nothing about it.
 */
export function tapReach(coarse: boolean): number {
  return coarse ? TAP_TARGET_PX / 2 : 0;
}

/**
 * Does this device point with a finger?
 *
 * `(pointer: coarse)` describes the *primary* pointer, which is the right
 * question: a laptop with a touchscreen answers false and keeps its precise
 * behaviour, and a phone answers true. Takes the matcher so a test can ask
 * both questions, and tolerates its absence — a browser too old to answer
 * gets the exact behaviour it has always had rather than an exception.
 */
export function coarsePointer(
  matcher: ((query: string) => { matches: boolean }) | undefined
    = typeof matchMedia === 'function' ? matchMedia : undefined,
): boolean {
  try {
    return matcher?.('(pointer: coarse)').matches ?? false;
  } catch {
    return false;
  }
}

/** A point on screen, in CSS pixels from the top left of the map canvas. */
export interface ScreenPoint {
  readonly x: number;
  readonly y: number;
}

/**
 * The box to ask MapLibre about: the tap, grown by `reach` in each direction.
 *
 * A box rather than a circle because that is what `queryRenderedFeatures`
 * takes. It is deliberately more generous than the rule — its corners reach
 * `reach * √2` — so it is a way of *gathering candidates*, never the answer.
 * `nearestWithin` below applies the actual rule, which is round.
 */
export function tapBox(point: ScreenPoint, reach: number): [ScreenPoint, ScreenPoint] {
  return [
    { x: point.x - reach, y: point.y - reach },
    { x: point.x + reach, y: point.y + reach },
  ];
}

/**
 * Which candidate the reader meant: the nearest centre within reach, or -1.
 *
 * Nearest rather than first, and this is the half that makes a wider target
 * safe. A city centre at walking zoom puts stops within a few pixels of each
 * other, so a widened query routinely catches two or three; taking whichever
 * MapLibre listed first would open a stop across the road from the one under
 * the finger, which is a worse failure than the one being fixed — it is
 * confidently wrong rather than unresponsive.
 *
 * Distances are compared squared. Not for speed at these counts, but because
 * it keeps the comparison exact: no square roots, so no candidate wins a tie
 * on a rounding error.
 *
 * A tie goes to the earlier candidate, which is MapLibre's render order, so
 * the mark drawn on top wins the coin toss — the same rule the eye would use.
 */
export function nearestWithin(
  candidates: readonly ScreenPoint[],
  point: ScreenPoint,
  reach: number,
): number {
  const limit = reach * reach;
  let best = -1;
  let bestDistance = 0;

  for (let i = 0; i < candidates.length; i += 1) {
    const c = candidates[i]!;
    const dx = c.x - point.x;
    const dy = c.y - point.y;
    const distance = dx * dx + dy * dy;
    if (distance > limit) continue;
    // Strictly closer wins, so the first of a tie keeps it.
    if (best === -1 || distance < bestDistance) {
      best = i;
      bestDistance = distance;
    }
  }

  return best;
}
